import { useState, useEffect, useMemo } from 'react';
import { calculateSalary, SalaryCalculationResult } from '../utils/salaryCalculations';
import { PrimesData, OverseasTerritoryId, CotisationsData, GradeData } from '../types';

interface UseSalaryCalculationsProps {
  selectedGrade: number | null;
  selectedEchelon: number | null;
  workTimePercentage: number;
  grades: GradeData[];
  primesData: PrimesData;
  cotisationsData: CotisationsData;
  selectedOverseasTerritory?: OverseasTerritoryId;
  isLoading?: boolean;
}

export function useSalaryCalculations({
  selectedGrade,
  selectedEchelon,
  workTimePercentage,
  grades,
  primesData,
  cotisationsData,
  selectedOverseasTerritory = null,
  isLoading = false
}: UseSalaryCalculationsProps) {
  const [isCalculating, setIsCalculating] = useState(false);
  const [salaryData, setSalaryData] = useState<SalaryCalculationResult | null>(null);

  // Calculate current indice majoré
  const currentIndiceMajore = useMemo(() => {
    if (!selectedGrade || selectedEchelon === null) return null;

    const currentEchelon = grades
      .find(grade => grade.id === selectedGrade)
      ?.echelons.find(e => e.echelon === selectedEchelon);

    return currentEchelon?.indiceMajoré || null;
  }, [selectedGrade, selectedEchelon, grades]);

  // Calculate salary data with useEffect to handle side effects
  useEffect(() => {
    if (isLoading || !selectedGrade || selectedEchelon === null || !currentIndiceMajore) {
      setSalaryData(null);
      return;
    }

    // Simulate calculation delay for better UX
    setIsCalculating(true);
    
    const timer = setTimeout(() => {
      const result = calculateSalary(
        selectedGrade,
        selectedEchelon,
        workTimePercentage,
        grades,
        primesData,
        selectedOverseasTerritory,
        cotisationsData
      );
      
      setSalaryData(result);
      setIsCalculating(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [
    isLoading,
    selectedGrade,
    selectedEchelon,
    currentIndiceMajore,
    workTimePercentage,
    grades,
    primesData,
    cotisationsData,
    selectedOverseasTerritory
  ]);

  // Validation helpers
  const isValidConfiguration = useMemo(() => {
    return selectedGrade !== null && currentIndiceMajore !== null;
  }, [selectedGrade, selectedEchelon]);

  const hasCompleteData = useMemo(() => {
    return isValidConfiguration && salaryData !== null;
  }, [isValidConfiguration, salaryData]);

  return {
    currentIndiceMajore,
    salaryData,
    isCalculating,
    isValidConfiguration,
    hasCompleteData
  };
}